import { TabPanel, TabPanels, Tabs } from "react-aria-components";
import { unstable_batchedUpdates } from "react-dom";
import { useCallback } from "react";

import { Tab2 } from "@/ui2/atoms/Tab";
import { TabList2 } from "@/ui2/atoms/TabList";

import { AllNotesPanel } from "./components/AllNotesPanel";
import type { CosignatureFilterProps } from "./(filters)/CosignatureFilters";
import { CosignatureModalProvider } from "./CosignatureModalProvider";
import { NeedsReviewPanel } from "./components/NeedsReviewPanel";
import { NeedsReviewTab } from "./components/NeedsReviewTab";
import { useCosignatureFilters } from "./(filters)/hooks/useCosignatureFilters";
import { useCosignatureSelectionActions } from "./CosignatureSelectionProvider";

type Props = { defaultSupervisorId: string };

export function CosignatureTabs({ defaultSupervisorId }: Props) {
  const { clearSelection } = useCosignatureSelectionActions();

  const [{ status, supervisor_id: supervisorIdFilter }, setFilters, resetFilters] =
    useCosignatureFilters({
      status: null,
      supervisor_id: defaultSupervisorId,
    });

  // null    → explicitly cleared: omit param, fetch all supervisors
  // string  → user-selected or default: use as-is
  const supervisorIdParam = supervisorIdFilter ?? undefined;

  // switching tabs has no local state to update — only clear the status filter and selection
  const handleTabSelectionChange = useCallback(() => {
    // React 16 does not batch updates fired from react-aria's interaction handlers
    unstable_batchedUpdates(() => {
      setFilters({ status: null });
      clearSelection();
    });
  }, [setFilters, clearSelection]);

  const filters: CosignatureFilterProps = {
    onStatusChange: (value) =>
      unstable_batchedUpdates(() => {
        setFilters({ status: value });
        clearSelection();
      }),
    onSupervisorChange: (value) =>
      unstable_batchedUpdates(() => {
        setFilters({ supervisor_id: value });
        clearSelection();
      }),
    status,
    supervisor_id: supervisorIdFilter,
  };

  const isFiltered = status !== null || supervisorIdFilter != null;
  const onClearFilters = isFiltered ? resetFilters : undefined;

  return (
    <CosignatureModalProvider onBatchSuccess={clearSelection}>
      <Tabs
        className="tw:flex tw:flex-col"
        defaultSelectedKey="needs-review"
        keyboardActivation="manual"
        onSelectionChange={handleTabSelectionChange}
      >
        <TabList2 aria-label="Co-signature notes" className="tw:px-6">
          <NeedsReviewTab
            cosignature_status={status ?? undefined}
            supervisor_id={supervisorIdParam}
          />
          <Tab2 id="all-notes">All Notes</Tab2>
        </TabList2>

        <TabPanels>
          <TabPanel id="needs-review">
            <NeedsReviewPanel
              filters={filters}
              onClearFilters={onClearFilters}
              tab="needs-review"
            />
          </TabPanel>

          <TabPanel id="all-notes">
            <AllNotesPanel filters={filters} onClearFilters={onClearFilters} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </CosignatureModalProvider>
  );
}
